import api from './api';
import type {
  TenantListItem,
  TenantDetail,
  CreateTenantRequest,
  UpdateTenantRequest,
  TenantFilters,
  TopupRequest,
  TopupRequestFilters,
  ProcessTopupRequest,
  PaginatedResponse,
  DashboardStats,
} from '@/types';

const TENANTS_ENDPOINTS = {
  LIST: '/admin/tenants',
  DETAIL: (id: string) => `/admin/tenants/${id}`,
  CREATE: '/admin/tenants',
  UPDATE: (id: string) => `/admin/tenants/${id}`,
  DELETE: (id: string) => `/admin/tenants/${id}`,
  SUSPEND: (id: string) => `/admin/tenants/${id}/suspend`,
  ACTIVATE: (id: string) => `/admin/tenants/${id}/activate`,
  TOPUP_REQUESTS: '/admin/topup-requests',
  PROCESS_TOPUP: (id: string) => `/admin/topup-requests/${id}/process`,
  DASHBOARD_STATS: '/admin/dashboard/stats',
};

export const tenantsService = {
  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await api.get<DashboardStats>(TENANTS_ENDPOINTS.DASHBOARD_STATS);
    return response.data;
  },

  /**
   * Get paginated list of all tenants
   */
  async getTenants(filters: TenantFilters = {}): Promise<PaginatedResponse<TenantListItem>> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.plan) params.append('plan', filters.plan);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await api.get<PaginatedResponse<TenantListItem>>(
      `${TENANTS_ENDPOINTS.LIST}?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get tenant by ID
   */
  async getTenantById(id: string): Promise<TenantDetail> {
    const response = await api.get<TenantDetail>(TENANTS_ENDPOINTS.DETAIL(id));
    return response.data;
  },

  /**
   * Create a new tenant
   */
  async createTenant(data: CreateTenantRequest): Promise<TenantDetail> {
    const response = await api.post<TenantDetail>(TENANTS_ENDPOINTS.CREATE, data);
    return response.data;
  },

  /**
   * Update tenant by ID
   */
  async updateTenant(id: string, data: UpdateTenantRequest): Promise<TenantDetail> {
    const response = await api.patch<TenantDetail>(TENANTS_ENDPOINTS.UPDATE(id), data);
    return response.data;
  },

  /**
   * Delete tenant by ID
   */
  async deleteTenant(id: string): Promise<void> {
    await api.delete(TENANTS_ENDPOINTS.DELETE(id));
  },

  /**
   * Suspend tenant by ID
   */
  async suspendTenant(id: string): Promise<TenantDetail> {
    const response = await api.post<TenantDetail>(TENANTS_ENDPOINTS.SUSPEND(id));
    return response.data;
  },

  /**
   * Activate tenant by ID
   */
  async activateTenant(id: string): Promise<TenantDetail> {
    const response = await api.post<TenantDetail>(TENANTS_ENDPOINTS.ACTIVATE(id));
    return response.data;
  },

  /**
   * Get paginated list of topup requests
   */
  async getTopupRequests(filters: TopupRequestFilters = {}): Promise<PaginatedResponse<TopupRequest>> {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.tenantId) params.append('tenantId', filters.tenantId);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await api.get<PaginatedResponse<TopupRequest>>(
      `${TENANTS_ENDPOINTS.TOPUP_REQUESTS}?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Process (approve/reject) a topup request
   */
  async processTopupRequest(id: string, data: ProcessTopupRequest): Promise<TopupRequest> {
    const response = await api.post<TopupRequest>(TENANTS_ENDPOINTS.PROCESS_TOPUP(id), data);
    return response.data;
  },
};

export default tenantsService;
